import { Controller } from "@hotwired/stimulus"

// "Invite" pill on the mobile Network tab: shares the practice's open invite
// link. In the native shell bridge/share_controller.js (listed first in
// data-action) hands it to the system share sheet; here the Web Share API
// does the same in Safari, and anywhere else the link is copied.
//
// Also keeps the pill clear of the network tracker, which sits sticky at the
// bottom of the scroller under the pill's usual spot.
export default class extends Controller {
  static values = { url: String, title: String, text: String }

  connect() {
    this.measure = this.measure.bind(this)
    this.page = this.element.closest(".mobile-network-page")
    this.scroller = this.page?.querySelector(".mobile-network-body")
    this.observer = new MutationObserver(() => requestAnimationFrame(this.measure))
    if (this.page) this.observer.observe(this.page, { childList: true, subtree: true })
    // The tracker is sticky: whether it sits under the pill changes with scroll.
    this.scroller?.addEventListener("scroll", this.measure, { passive: true })
    window.addEventListener("resize", this.measure)
    this.measure()
  }

  disconnect() {
    this.observer?.disconnect()
    this.scroller?.removeEventListener("scroll", this.measure)
    window.removeEventListener("resize", this.measure)
  }

  async share(event) {
    event.preventDefault()
    if (this.nativeHandlesShare) return

    const payload = { title: this.titleValue, text: this.textValue, url: this.urlValue }
    if (navigator.share) {
      try { await navigator.share(payload) } catch (_) { /* dismissed */ }
      return
    }

    try {
      await navigator.clipboard.writeText(`${this.textValue} ${this.urlValue}`)
      this.flash("Invite link copied")
    } catch (_) {
      window.prompt("Copy your invite link", this.urlValue)
    }
  }

  // Only the part of the tracker stuck to the scroller's bottom edge is in
  // the pill's way; when the tracker scrolls up into the list, nothing is.
  measure() {
    if (!this.page || !this.scroller) return
    const tracker = this.page.querySelector(".mobile-network-tracker")
    let overlap = 0
    if (tracker) {
      const rect = tracker.getBoundingClientRect()
      const bottom = this.scroller.getBoundingClientRect().bottom
      if (rect.bottom >= bottom - 1) overlap = Math.max(0, bottom - rect.top)
    }
    this.page.style.setProperty("--network-tracker-h", `${Math.round(overlap)}px`)
  }

  flash(text) {
    const toast = document.createElement("div")
    toast.className = "mobile-toast"
    toast.textContent = text
    document.body.appendChild(toast)
    requestAnimationFrame(() => toast.classList.add("is-visible"))
    setTimeout(() => { toast.classList.remove("is-visible"); setTimeout(() => toast.remove(), 300) }, 2200)
  }

  get nativeHandlesShare() {
    const match = navigator.userAgent.match(/bridge-components: \[(.*?)\]/)
    return !!match && match[1].split(" ").includes("share")
  }
}
